export enum EDuplicateEntityType {
  PEOPLE = 'PEOPLE',
  ORGANIZATION = 'ORGANIZATION',
}

export enum EDuplicateStatus {
  UNREVIEWED = 'UNREVIEWED',
  IN_REVIEW = 'IN_REVIEW',
  MERGED = 'MERGED',
  DISMISSED = 'DISMISSED',
}

export enum EMergeStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  SIMULATED = 'SIMULATED',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  RESTORED = 'RESTORED',
}

export enum EFailureReasonCode {
  UNIQUE_CONFLICT = 'UNIQUE_CONFLICT',
  VERSION_STALE = 'VERSION_STALE',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
}

export enum ERestoreOutcomeStatus {
  RESTORED = 'RESTORED',
  FAILED_PAST_RETENTION = 'FAILED_PAST_RETENTION',
  FAILED_INVALID_STATE = 'FAILED_INVALID_STATE',
  FAILED_LEGACY_SCHEMA = 'FAILED_LEGACY_SCHEMA',
}

export enum EDuplicateMatchedField {
  LAST_NAME = 'last_name',
  EMAIL = 'email',
  PHONE = 'phone',
}

export type TPeopleMatchCriteria = {
  rule: 'two_of_three';
  matched_fields: EDuplicateMatchedField[];
};

export type TOrgNormalizedAddressMatchCriteria = {
  rule: 'normalized_address_exact';
  normalized_address: string;
};

export type TOrgFuzzyMatchCriteria = {
  rule: 'fuzzy_name_zip_city';
  name_similarity: number;
  city: string;
  zip_code: string;
};

export type TMatchCriteria =
  | TPeopleMatchCriteria
  | TOrgNormalizedAddressMatchCriteria
  | TOrgFuzzyMatchCriteria;

export type TDuplicatePairDB = {
  id: string;
  entityType: EDuplicateEntityType;
  recordAId: string;
  recordBId: string;
  // Display-name enrichment landed in GROW-4069. PEOPLE → "First Last";
  // ORGANIZATION → org name. Null when the underlying record is missing.
  recordADisplayName: string | null;
  recordBDisplayName: string | null;
  matchCriteria: string;
  status: EDuplicateStatus;
  detectedAt: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
  dismissalReason: string | null;
  dismissedCount: number;
  createdAt: string;
  updatedAt: string;
};

export type TDuplicatePairUI = Omit<TDuplicatePairDB, 'matchCriteria'> & {
  matchCriteria: TMatchCriteria | null;
};

export type TSearchDuplicatesRequestUI = {
  entityType: EDuplicateEntityType;
  status?: EDuplicateStatus;
  query?: string;
  page?: number;
  size?: number;
  sortBy?: 'detectedAt' | 'createdAt' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
};

export type TSearchDuplicatesResponseDB = {
  duplicates: TDuplicatePairDB[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
};

export type TSearchDuplicatesResponseUI = {
  duplicates: TDuplicatePairUI[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
};

export type TTriggerDetectionRequestUI = {
  entityType: EDuplicateEntityType;
  rescan?: boolean;
};

export type TTriggerDetectionResponseDB = {
  status: 'accepted' | 'rejected' | 'conflict';
  jobId: string | null;
  message: string | null;
};

export type TTriggerDetectionResponseUI = TTriggerDetectionResponseDB;

export type TMergeRecordsRequestUI = {
  entityType: EDuplicateEntityType;
  primaryRecordId: string;
  secondaryRecordIds: string[];
  dryRun?: boolean;
};

export type TMergeRecordsResponseDB = {
  mergeHistoryId: string | null;
  status: EMergeStatus;
  primaryRecordId: string;
  message: string | null;
  fieldDeltas: string | null;
  relatedEntitiesUpdated: string | null;
};

export type TMergeRecordsResponseUI = Omit<
  TMergeRecordsResponseDB,
  'fieldDeltas' | 'relatedEntitiesUpdated'
> & {
  fieldDeltas: Record<string, unknown> | null;
  relatedEntitiesUpdated: Record<string, unknown> | null;
};

export type TSwapRecordsRequestUI = {
  duplicatePairId: string;
};

export type TDismissDuplicateRequestUI = {
  duplicatePairId: string;
  reason?: string;
};

export type TSearchMergeHistoryRequestUI = {
  entityType?: EDuplicateEntityType;
  initiatedBy?: string;
  status?: EMergeStatus;
  startDate?: string;
  endDate?: string;
  page?: number;
  size?: number;
  sortBy?: 'initiatedAt' | 'completedAt' | 'createdAt';
  sortDirection?: 'asc' | 'desc';
};

export type TMergeHistoryEntryDB = {
  id: string;
  entityType: EDuplicateEntityType;
  primaryRecordId: string;
  mergedRecordIds: string;
  initiatedBy: string;
  initiatedAt: string;
  completedAt: string | null;
  status: EMergeStatus;
  failureReasonCode: EFailureReasonCode | null;
  failureDetails: string | null;
  fieldDeltas: string | null;
  relatedEntitiesUpdated: string | null;
  createdAt: string;
};

export type TMergeHistoryEntryUI = Omit<
  TMergeHistoryEntryDB,
  'mergedRecordIds' | 'fieldDeltas' | 'relatedEntitiesUpdated'
> & {
  mergedRecordIds: string[];
  fieldDeltas: Record<string, unknown> | null;
  relatedEntitiesUpdated: Record<string, unknown> | null;
};

export type TSearchMergeHistoryResponseDB = {
  mergeHistory: TMergeHistoryEntryDB[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
};

export type TSearchMergeHistoryResponseUI = {
  mergeHistory: TMergeHistoryEntryUI[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
};

export type TRestoreMergeRequestUI = {
  mergeHistoryId: string;
};

export type TRestoreMergeResponseDB = {
  mergeHistoryId: string;
  status: ERestoreOutcomeStatus;
  message: string | null;
  restoredAt: string | null;
  restoredBy: string | null;
  restorationWarnings: string[];
};

export type TRestoreMergeResponseUI = TRestoreMergeResponseDB;
